
import React from 'react';
import Logo from '../components/Logo.tsx';

interface LandingProps {
  onGetStarted: () => void;
  onDemo: () => void;
}

const LandingView: React.FC<LandingProps> = ({ onGetStarted, onDemo }) => {
  return (
    <div className="min-h-screen bg-white font-sans selection:bg-blue-100">
      <nav className="p-8 flex justify-between items-center max-w-7xl mx-auto border-b border-slate-50">
        <Logo variant="dark" />
        <div className="flex items-center gap-8">
           <button onClick={onDemo} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-blue-600 transition">Demo Hub</button>
           <button 
             onClick={onGetStarted}
             className="px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest bg-blue-950 text-white hover:bg-blue-800 transition shadow-xl"
           >
             Sign In
           </button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-8 pt-24 pb-32">
        <div className="text-center max-w-4xl mx-auto mb-24">
          <div className="inline-block px-5 py-2 bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-[0.2em] rounded-full border border-blue-100 mb-8 animate-in slide-in-from-bottom-2">
            Professional Synergy Protocol
          </div>
          <h1 className="text-7xl md:text-9xl font-black text-slate-950 mb-10 tracking-tighter leading-[0.85] animate-in slide-in-from-bottom-4 duration-700">
            Network <br/>
            <span className="text-blue-600">Intelligently.</span>
          </h1>
          <p className="text-xl text-slate-500 mb-14 leading-relaxed font-medium max-w-2xl mx-auto animate-in slide-in-from-bottom-8 duration-1000">
            Bridging academia and industry through AI-orchestrated coffee chats. Verified mentors from top-tier firms.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center animate-in slide-in-from-bottom-10 duration-1000">
            <button 
              onClick={onDemo}
              className="w-full sm:w-auto px-14 py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg hover:bg-blue-500 shadow-3xl shadow-blue-200 transition-all hover:-translate-y-1 flex items-center justify-center gap-4"
            >
              <i className="fas fa-play text-sm"></i>
              Launch Experience
            </button>
            <button 
              onClick={onGetStarted}
              className="w-full sm:w-auto px-14 py-6 bg-white text-blue-950 border-2 border-slate-100 rounded-[2rem] font-black text-lg hover:bg-slate-50 transition"
            >
              Create Account
            </button>
          </div>
        </div>

        {/* Static Feature Highlight (Replaces AI Video Journey) */}
        <div className="max-w-5xl mx-auto mb-32 group">
          <div className="bg-slate-950 rounded-[4rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] relative aspect-video flex items-center justify-center border border-white/5">
             <div className="p-16 text-center">
                <div className="w-28 h-28 bg-white/5 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 text-4xl text-white/20 border border-white/5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-700">
                   <i className="fas fa-brain"></i>
                </div>
                <h2 className="text-4xl font-black text-white mb-4 tracking-tighter">Experience Sage AI.</h2>
                <p className="text-slate-400 text-lg mb-8 max-w-md mx-auto font-medium">
                  Your dedicated AI career architect. Sage orchestrates connections, drafts outreach protocols, and builds your strategic roadmap.
                </p>
                <div className="flex gap-2 justify-center">
                    <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-black text-blue-300 uppercase tracking-widest border border-white/5">Career Strategy</span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-[10px] font-black text-emerald-300 uppercase tracking-widest border border-white/5">Smart Matching</span>
                </div>
             </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 border-t border-slate-100 pt-32">
           <div className="space-y-8">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl shadow-sm"><i className="fas fa-wand-sparkles"></i></div>
              <h3 className="text-2xl font-black text-slate-950 tracking-tight">AI Coached Outreach.</h3>
              <p className="text-slate-500 font-medium leading-relaxed">Sage drafts the perfect request based on your aspirations and the professional's unique journey.</p>
           </div>
           <div className="space-y-8">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl shadow-sm"><i className="fas fa-chess-knight"></i></div>
              <h3 className="text-2xl font-black text-slate-950 tracking-tight">Strategic Blueprinting.</h3>
              <p className="text-slate-500 font-medium leading-relaxed">Receive a detailed roadmap and meeting prep guide for every high-stakes conversation.</p>
           </div>
           <div className="space-y-8">
              <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center text-2xl shadow-sm"><i className="fas fa-shield-halved"></i></div>
              <h3 className="text-2xl font-black text-slate-950 tracking-tight">Verified Synergy.</h3>
              <p className="text-slate-500 font-medium leading-relaxed">Our matching engine prioritizes cultural and technical alignment, ensuring high-value connections.</p>
           </div>
        </div>
      </main>
    </div>
  );
};

export default LandingView;
